'use client';

import type { MonolithPayInValidationResponse, MonolithPaymentsPlayerDataGetResponse } from '@payments/aurum-contracts';
import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { Button } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import { Checkbox } from '../../../../../form-controls/Checkbox/Checkbox';
import { TextInput } from '../../../../../form-controls/TextInput/TextInput';
import { Link } from '../../../../../Link/Link';
import { DEPOSIT_PAYMENT_METHODS } from '../../../../../PaymentMethodSelector/PaymentMethods';
import { PaymentMethodSelector } from '../../../../../PaymentMethodSelector/PaymentMethodSelector';
import type { MethodOrInstrument } from '../../../../../PaymentMethodSelector/types';
import { StatusNotification } from '../../../../../StatusNotification/StatusNotification';
import type { CheckoutSummaryStepProps } from '../../types';
import {
  CHECKOUT_DIALOG_TRACKING,
  buildCheckoutPreStepContinueTracking,
  buildEmbeddedCashierAmountContainerTracking,
  buildEmbeddedCashierContinueButtonTracking,
  createEmbeddedCashierContinueHandler,
  getEmbeddedCashierVariant,
  hasEmbeddedCashierContinueTracking,
  isBelowMinimumPreStep,
} from '../../utils/checkoutDialogTracking';
import { getIsPayDisabled } from '../../utils/getIsPayDisabled';
import { CheckoutPayButton } from './components/CheckoutPayButton/CheckoutPayButton';
import { CheckoutSummary } from './components/CheckoutSummary/CheckoutSummary';

function CheckoutSummaryStep({
  cartSummary,
  ticketDetails,
  isSubmitting,
  scenario,
  depositAmount,
  depositTrackingType,
  onDepositAmountChange,
  onPay,
  onOpenDepositLimits,
}: CheckoutSummaryStepProps) {
  const { payable, usedBalance, bonusDiscount } = cartSummary;
  const [selectedMethod, setSelectedMethod] = useState<MethodOrInstrument | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsValidationAttempted, setTermsValidationAttempted] = useState(false);
  const [hasAcknowledgedBelowMinimum, setHasAcknowledgedBelowMinimum] = useState(false);
  const t = useTranslations('global_sheets.deposit');
  const tCheckout = useTranslations('global_sheets.checkout');
  const tGeneral = useTranslations('general');
  const formatter = useCurrencyFormatter();
  const { requiresFirstTimeDepositTermsAcceptance, showDepositLimitButtonOnDepositAmountStep } = getSiteConfig().PAYMENTS_FLAGS;

  const { data: paymentsPlayersData, isFetched: isPaymentsPlayersFetched } = useApiQuery<MonolithPaymentsPlayerDataGetResponse>(API_ROUTES.PAYMENTS.PAYMENTS_PLAYERS, {
    credentials: true,
  });

  const { data: paymentsLimits } = useApiQuery<MonolithPayInValidationResponse>(API_ROUTES.PAYMENTS.DEPOSIT_LIMITS, {
    credentials: true,
  });

  const netDepositLimit = paymentsLimits?.limits?.find(limit => limit.limitType === 'NET_DEPOSIT');
  const isDepositLimitFullyReached = !!netDepositLimit && Number(netDepositLimit.remainingAmount || 0) <= 0;
  const parsedDepositAmount = Number(depositAmount || '0');
  const depositAmountPence = Number.isNaN(parsedDepositAmount) ? 0 : Math.round(parsedDepositAmount * 100);
  const amountExceedsDepositLimit = !!netDepositLimit && Number(netDepositLimit.remainingAmount || 0) > 0 && depositAmountPence > Number(netDepositLimit.remainingAmount);

  const showTermsCheckbox = requiresFirstTimeDepositTermsAcceptance && isPaymentsPlayersFetched && (paymentsPlayersData?.hasToAcceptTermsAndConditions ?? false);
  const isTermsRequirementMet = !showTermsCheckbox || termsAccepted;
  const showTermsError = showTermsCheckbox && termsValidationAttempted && !termsAccepted;

  const selectedMethodValue = selectedMethod?.apiReference ?? selectedMethod?.name ?? '';

  const handleMethodChange = useCallback((method: MethodOrInstrument) => {
    setSelectedMethod(method);
  }, []);

  const getCtaLabel = (): string => {
    if (payable === 0 && usedBalance === 0 && bonusDiscount > 0) {
      return tCheckout('pay_with_voucher');
    }

    if (payable === 0 && usedBalance > 0) {
      return tCheckout('pay_with_balance');
    }

    return tCheckout('pay');
  };

  const isApplePay = selectedMethod?.name === DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name;
  const showPaymentInstruments = payable > 0;
  const embeddedCashierVariant = getEmbeddedCashierVariant(scenario, payable);
  const isBelowMinimumPreStepActive = isBelowMinimumPreStep(embeddedCashierVariant, payable, hasAcknowledgedBelowMinimum);
  const showPaymentMethodSelector = showPaymentInstruments && !isBelowMinimumPreStepActive;
  const effectiveMin = selectedMethod?.minAllowedAmount;
  const showDepositInput = showPaymentInstruments && selectedMethod != null && effectiveMin != null && payable < effectiveMin;
  const parsedDeposit = Number(depositAmount || '0');
  const amountPence = Number.isNaN(parsedDeposit) ? 0 : Math.round(parsedDeposit * 100);
  const isBelowMinimum = showDepositInput && effectiveMin != null && amountPence < effectiveMin;
  const addToBalance = showDepositInput ? Math.max(0, amountPence - payable) : undefined;
  const isWaitingForTermsData = requiresFirstTimeDepositTermsAcceptance && !isPaymentsPlayersFetched;
  const isPayDisabled = getIsPayDisabled({
    isSubmitting,
    isWaitingForTermsData,
    embeddedCashierVariant,
    isTermsRequirementMet,
    isBelowMinimumPreStepActive,
    isBelowMinimum,
    isDepositLimitFullyReached,
    amountExceedsDepositLimit,
  });

  const handleEmbeddedCashierContinue = () => {
    if (showTermsCheckbox && !termsAccepted) {
      setTermsValidationAttempted(true);
      return;
    }

    onPay(selectedMethod);
  };

  const hasTrackedContinueButton = hasEmbeddedCashierContinueTracking(embeddedCashierVariant);

  let payButtonOnClick = () => onPay(selectedMethod);
  let payButtonTrackingProps: Record<string, string> | undefined;

  if (embeddedCashierVariant && hasTrackedContinueButton) {
    if (isBelowMinimumPreStepActive) {
      payButtonOnClick = () => setHasAcknowledgedBelowMinimum(true);
      payButtonTrackingProps = buildCheckoutPreStepContinueTracking();
    } else {
      payButtonOnClick = createEmbeddedCashierContinueHandler(embeddedCashierVariant, {
        onPay,
        selectedMethod,
        onNoBalanceContinue: handleEmbeddedCashierContinue,
      });
      payButtonTrackingProps = buildEmbeddedCashierContinueButtonTracking(embeddedCashierVariant, {
        stakeInMinorUnits: cartSummary.stake,
        selectedMethod,
        depositAmount,
        depositType: depositTrackingType,
      });
    }
  }

  return (
    <div
      className="flex flex-col flex-1 gap-4 px-5 py-4"
      data-testid="checkout-summary-step"
      data-track-container={isBelowMinimumPreStepActive ? 'Pre Step' : 'Amount'}
      {...(isBelowMinimumPreStepActive ? {} : buildEmbeddedCashierAmountContainerTracking(embeddedCashierVariant, depositTrackingType))}
    >
      {isDepositLimitFullyReached && netDepositLimit ? (
        <StatusNotification
          label={t('deposit_limits.net_deposit_limit_message_fully_reached_title')}
          description={t('deposit_limits.net_deposit_limit_message_fully_reached_description', {
            date: new Date(netDepositLimit.expirationTime).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
            time: new Date(netDepositLimit.expirationTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          })}
          type="warning"
          variant="fixed"
          dataTrackType={CHECKOUT_DIALOG_TRACKING.depositLimitReached.dataTrackType}
          dataTrackMessage={CHECKOUT_DIALOG_TRACKING.depositLimitReached.dataTrackMessage}
        />
      ) : (
        <>
          {showPaymentMethodSelector && (
            <div className="flex flex-col gap-4 mb-4">
              <PaymentMethodSelector type="deposit" value={selectedMethodValue} onChange={handleMethodChange} />
            </div>
          )}

          {showDepositInput && effectiveMin != null && (
            <TextInput
              id="checkout-deposit-amount"
              data-testid="checkout-deposit-amount"
              type="number"
              min="0"
              step="0.01"
              leftIcon={tGeneral('currency_symbol')}
              label={t('checkout_deposit.label')}
              value={depositAmount}
              disabled={isSubmitting}
              onChange={e => {
                const value = e.target.value;
                if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                  onDepositAmountChange(value);
                }
              }}
              helpMessage={
                isBelowMinimum
                  ? t('checkout_deposit.below_minimum_error', {
                      amount: formatter(effectiveMin).format(NumberFormats.WITH_DECIMALS).get(),
                    })
                  : undefined
              }
              helpMessageType={isBelowMinimum ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'Amount',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Amount',
              }}
            />
          )}

          {amountExceedsDepositLimit && netDepositLimit ? (
            <StatusNotification
              label={t('deposit_limits.net_deposit_limit_message_partially_reached_title')}
              description={t('deposit_limits.net_deposit_limit_message_partially_reached_description', {
                amount: formatter(netDepositLimit.remainingAmount || 0)
                  .format(NumberFormats.WITH_DECIMALS)
                  .get(),
              })}
              type="warning"
              variant="fixed"
              dataTrackType={CHECKOUT_DIALOG_TRACKING.depositLimitAmountExceeded.dataTrackType}
              dataTrackMessage={CHECKOUT_DIALOG_TRACKING.depositLimitAmountExceeded.dataTrackMessage}
            />
          ) : null}

          <CheckoutSummary cartSummary={cartSummary} ticketDetails={ticketDetails} addToBalance={addToBalance} />

          {showTermsCheckbox && (
            <Checkbox
              className="my-4"
              id="checkout-terms-accepted"
              checked={termsAccepted}
              onCheckedChange={checked => {
                setTermsAccepted(!!checked);
                if (checked) {
                  setTermsValidationAttempted(false);
                }
              }}
              disabled={isSubmitting}
              data-testid="terms-checkbox"
              helpMessage={showTermsError ? t('terms.required_error') : undefined}
              helpMessageType={showTermsError ? 'error' : undefined}
              helperTrackingProps={{
                'data-track-helper': 'Agree',
              }}
              label={
                <p className="paragraph-s">
                  <span>{t('terms.text_1')}</span>
                  <Link href={ROUTES.FOOTER.LEGAL.TERMS} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="Terms and Conditions">
                    {t('terms.link_1')}
                  </Link>
                  <span>{t('terms.text_2')}</span>
                  <Link href={ROUTES.FOOTER.LEGAL.GAMBLING_COMMISSION} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="Funds Protection">
                    {t('terms.link_2')}
                  </Link>
                  <span>{t('terms.text_3')}</span>
                  <Link href={ROUTES.FOOTER.LEGAL.DOCUMENTS} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="Verified Documents">
                    {t('terms.link_3')}
                  </Link>
                  <span>{t('terms.text_4')}</span>
                </p>
              }
              alignment="start"
              trackingProps={{
                'data-track-name': 'Agree',
              }}
            />
          )}

          <CheckoutPayButton
            isSubmitting={isSubmitting}
            isApplePay={isApplePay}
            ctaLabel={getCtaLabel()}
            applePayPayWithLabel={t('apple_pay_button.pay_with')}
            applePayPayLabel={t('apple_pay_button.pay')}
            isDisabled={isPayDisabled}
            onClick={payButtonOnClick}
            trackingProps={payButtonTrackingProps}
            showActionsContainer={hasTrackedContinueButton}
          />

          {showDepositLimitButtonOnDepositAmountStep ? (
            <Button type="button" variant="ghost" className="w-full" onClick={onOpenDepositLimits} data-track-name="Deposit Limits" data-testid="deposit-limit-button">
              {t('deposit_limits.button')}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

export { CheckoutSummaryStep };
